import { RouteConfigComponentProps } from "react-router-config";
import { Form, Modal } from "antd";
import { useState, useContext, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useParams } from "react-router";
import moment from "moment";

import useRenovationPreloadForm from "../hooks/useRenovationPreloadForm";
import useSelectInput from "../hooks/useSelectInput";
import { ContextApp, CustomMessage, EnumsValues } from "../../../shared";
import { FORMAT_DATE_TIME_4 } from "../../../shared/MomentJS";
import {
  ClientInformationForm,
  GeneralDataForm,
  InformationForm,
  LegalPersonForm,
  NaturalPersonForm,
  UserInformationForm,
  VehicleDataForm,
  PledgeCreditorDataForm,
  QuoteForm,
  FormButtons,
  LegalRepresentativeForm,
} from "../components";
import {
  IEmissionParams,
  ILegalPersonParams,
  ILegalRepresentativeParams,
  INaturalPersonParams,
  IPledgedCreditorParams,
} from "../../../services/EmissionParamsForm";
import useCuitVerification from "../../../shared/hooks/useCuitVerification";
import useVehicleForm from "../hooks/useVehicleForm";
import {
  IEmissionErrorResponse,
  IUploadedFiles,
  IValuesEmisionRenovationForm,
} from "../interfaces/IValuesEmisionRenovationForm";
import useAgeVerification from "../../../shared/hooks/useAgeVerification";
import GraphqlService, {
  IFileData,
} from "../../../services/graphql/GraphqlService";
import { formNames } from "../utils/formNames";
import { zeroPad } from "../../../shared/utils";
import { IQuotationRequest } from "../../../services/IQuotation";
import { IProducer } from "../../../services/Producer";
import useEmissionPreloadForm from "../hooks/useEmissionPreloadForm";
import VehiclePicture from "../components/VehiclePicture";
import { IVehicleType } from "../../../services/IVehicleType";
import { useCheckAuthority } from "../../../shared/CustomHooks";

import "../styles/style.less";
import ModalLifePolicy from "../../ModalLifePolicy/ModalLifePolicy";

const RenovationFormPage = (
  routeProps: RouteConfigComponentProps
): JSX.Element => {
  const { ageVerification, isAdult } = useAgeVerification();
  const { customRequest, Mutation, customFileRequest } = GraphqlService();
  const { messageError, messageModalError } = CustomMessage();
  const [quotationId, setQuotationId] = useState<number>();
  const [form] = Form.useForm();
  const { user } = useContext(ContextApp);
  const { verifyCuit, isLoading: isLoadingCuitVerification } =
    useCuitVerification();
  const params: { id?: string } = useParams();
  const location = useLocation();
  const [formType, setFormType] = useState<string>("");
  const [renovationBlocked, setRenovationBlocked] = useState<boolean>(false);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false,
  });

  const type = () => {
    if (params.id && location.pathname.includes("/policies-to-renewed-list")) {
      setFormType("policies-to-renewed-list");
    } else if (params.id && location.pathname.includes("/direct-emission")) {
      setFormType("direct-emission-read-only");
      if (location.search.includes("?historical")) {
        setFormType("direct-emission-historical-read-only");
      }
    } else if (
      params?.id &&
      location.pathname.includes("/renewed-quotes-list")
    ) {
      setFormType("renewed-quotes-list");
      if (location.search.includes("?quotationId=")) {
        setQuotationId(Number(location.search.split("=")[1]));
      }
    } else if (!params?.id && location.pathname.includes("/direct-emission")) {
      setFormType("direct-emission");
    }
  };

  useEffect(() => {
    if (location && params) {
      type();
    }
  }, [params, location]);

  const [fiscalCondition, setFiscalCondition] = useState<string>();

  const { renovationData, quotation } = useRenovationPreloadForm({
    form,
    setFiscalCondition,
    renovationId:
      params.id && location.pathname.includes("/renovation")
        ? Number(params?.id)
        : undefined,
    quotationId,
  });

  const { emissionReadOnly, emission } = useEmissionPreloadForm({
    form,
    emissionId:
      params.id && location.pathname.includes("/direct-emission")
        ? Number(params?.id)
        : undefined,
    formType,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [personType, setPersonType] = useState<string>("");
  const [quotationResponse, setQuotationResponse] =
    useState<IQuotationRequest>();
  const [amountOutOfRange, setAmountOutOfRange] = useState<boolean>(false);
  const [accesoriesError, setAccesoriesError] = useState<boolean>(false);
  const [additionalHail, setAdditionalHail] = useState<boolean>(false);
  const [billingCode, setBillingCode] = useState<string>("");
  const [selectedProducer, setSelectedProducer] = useState<IProducer>();
  const [selectedVehicleType, setSelectedVehicleType] =
    useState<IVehicleType>();
  const [uploadedFiles, setUploadedFiles] = useState<IUploadedFiles>();
  const [errorResponse, setErrorResponse] = useState<IEmissionErrorResponse>({
    message: "",
    status_code: 0,
  });

  const {
    vehicleBrand,
    brandOptions,
    getBrandModel,
    vehicleModelOptions,
    vehicleModel,
    getYearsByBrandAndModel,
    yearOptions,
    yearList,
    getBrandModelByBrandModelYear,
    vehicleVersionOptions,
    vehicleVersionList,
    clearOnChangeBrand,
    clearOnChangeModel,
    clearOnChangeYear,
    getVehicleType,
    vehicleTypeOptions,
    vehicleTypes,
    destinyOptions,
    destinos,
    clearOnChangeVersion,
    clearOnChangeVehicleType,
    getDestinos,
  } = useVehicleForm({ form, renovationData, formType });

  const {
    fuelTypes,
    fuelOptions,
    bodyworkTypes,
    gendersTypes,
    genderOptions,
    paymentOptions,
    nationalityOptions,
    minBornTime,
    idsLiderarForMotorcycles,
    fiscalConditionOptions,
    provinces,
    provinceOptions,
    bodyWorkOptions,
    coveragePeriedOptions,
    coverageType,
    coverageTypeOptions,
    provinceOptionsId,
    adjustmentRateOptions,
    adjustmentRates,
    getFuelTypes,
    getBodyworkTypes,
    getCoverageTypes,
    paymentInstallmentTypeOptions,
    resetCoveragePeriods,
  } = useSelectInput({ renovationData, formType });

  const contextName = renovationData ? "Renovation" : "Emission";

  const saveImages = () => {
    if (uploadedFiles && Object.keys(uploadedFiles).length) {
      Object.keys(uploadedFiles).forEach((fileKey) => {
        const file = uploadedFiles[fileKey];
        const variables: any = {
          input: {
            file_type_id: EnumsValues.FileType.IMAGEN_PATENTE_POLIZA,
          },
          licensePlate: form.getFieldValue([
            formNames.vehicleData.VEHICLE_PATENT.name,
          ]),
        };
        if (fileKey && file) {
          variables.file = null;
          let fileArray: IFileData[] = [];
          fileArray = [
            {
              file,
              path: "variables.file",
            },
          ];

          try {
            const response = customFileRequest(
              {
                mutation: Mutation.saveFile,
                variables,
              },
              fileArray
            );
            if (!response) {
              throw new Error();
            }
          } catch (error: any) {
            messageModalError({
              context: `${contextName}.onFinish.2`,
              message:
                error?.message || `Error al cargar la imagen ${file.name}`,
            });
          }
        }
      });
    }
  };

  const responseModal = (
    title: string, 
    id: string, 
    life_id: string | null,
    life_obs: string | null,
  ) => {
    Modal.info({
      title,
      content: <>
        <p>Número de póliza: {id}</p>
        {life_id && <p>Número de póliza de vida: {life_id}</p>}
        {life_obs && <p>Observación póliza de vida: {life_obs}</p>}
      </>,
      className: "AutoClosableModal",
      centered: true,
    });
  };

  const getAccesories = () => {
    const code_acc: string[] = [];
    const value_acc: string[] = [];

    const gnc: number = form.getFieldValue(
      formNames.generalDataAccessories.ACCESSORIES_GNC.name
    );

    const tires: number = form.getFieldValue(
      formNames.generalDataAccessories.ACCESSORIES_SPECIAL_RIM.name
    );

    const freezer: number = form.getFieldValue(
      formNames.generalDataAccessories.ACCESSORIES_FREEZER.name
    );

    const cupula: number = form.getFieldValue(
      formNames.generalDataAccessories.ACCESSORIES_CUPULA.name
    );

    if (gnc) {
      code_acc.push(EnumsValues.accessoryIdLiderar.gnc);
      value_acc.push(String(gnc));
    }

    if (tires) {
      code_acc.push(EnumsValues.accessoryIdLiderar.tires);
      value_acc.push(String(tires));
    }

    if (freezer) {
      code_acc.push(EnumsValues.accessoryIdLiderar.freezer);
      value_acc.push(String(freezer));
    }

    if (cupula) {
      code_acc.push(EnumsValues.accessoryIdLiderar.cupula);
      value_acc.push(String(cupula));
    }

    return { code_acc, value_acc };
  };

  const validateForm = () => {
    
    if (!form.getFieldValue(formNames.information.PRODUCER.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Seleccione un productor",
      });

      return false;
    }

    if (!form.getFieldValue(formNames.clientInformation.CUIT_CUIL.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Seleccione un CUIT/CUIL",
      });
      return false;
    }

    if (
      !form.getFieldValue(
        formNames.clientInformation.COMPANY_NAME_OR_SURNAME.name
      )
    ) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Ingrese nombre y apellido",
      });
      return false;
    }

    if (
      !form.getFieldValue(formNames.clientInformation.FISCAL_CONDITION.name)
    ) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Seleccione una condición fiscal",
      });
      return false;
    }

    if (!form.getFieldValue(formNames.generalData.VEHICLE_BRAND.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Seleccione una marca de vehículo",
      });
      return false;
    }

    if (!form.getFieldValue(formNames.generalData.VEHICLE_MODEL.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Seleccione un modelo de vehículo",
      });
      return false;
    }

    if (!form.getFieldValue(formNames.generalData.VEHICLE_YEAR.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Seleccione año de vehículo",
      });
      return false;
    }

    if (!form.getFieldValue(formNames.generalData.VEHICLE_VERSION.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Seleccione versión de vehículo",
      });
      return false;
    }

    if (!form.getFieldValue(formNames.generalData.VEHICLE_TYPE.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Seleccione tipo de vehículo",
      });
      return false;
    }

    if (!form.getFieldValue(formNames.generalData.DESTINY.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Seleccione Destino",
      });

      return false;
    }

    if (!form.getFieldValue(formNames.generalData.PRODUCT_CODE.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message:
          "Código de producto inválido, revise los datos ingresados en productor y tipo de vehículo",
      });

      return false;
    }

    if (!form.getFieldValue(formNames.generalData.BODYWORK_TYPE.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Seleccione tipo de carrocería",
      });
      return false;
    }

    if (!form.getFieldValue(formNames.generalData.COVERAGE.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.1",
        message: "Seleccione un tipo de cobertura",
      });
      return false;
    }

    if (!form.getFieldValue(formNames.generalData.COVERAGE_TIME.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.2",
        message: "Seleccione un periodo de cobertura",
      });
      return false;
    }

    if (!form.getFieldValue(formNames.generalData.ADJUSTMENT_RATE.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.3",
        message: "Seleccione un tipo de tasa de ajuste",
      });
      return false;
    }

    if (!form.getFieldValue(formNames.userInformation.USER_PROVINCE.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.4",
        message: "Seleccione una provincia",
      });
      return false;
    }

    if (!form.getFieldValue(formNames.userInformation.USER_LOCATION.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.4",
        message: "Seleccione una localidad",
      });
      return false;
    }
    if (!form.getFieldValue("REMOLQUE_CC")) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.5",
        message: "Seleccione remolque",
      });
      return false;
    }
    

    if (!form.getFieldValue(formNames.userInformation.USER_POSTAL_CODE.name)) {
      messageError({
        context: "RenovationFormPage.getQuotation.error.4",
        message: "Seleccione un código postal",
      });
      return false;
    }

    return true;
  }
  const getQuotation = async () => {
    if (!validateForm()) return;
    const { code_acc, value_acc } = getAccesories();

    try {
      setIsLoading(true);
      
      const response: IQuotationRequest = await customRequest({
        query: Mutation.createQuotationRequest,
        variables: {
          input: {
            ADIC_GRANIZO: additionalHail ? "S" : "N",
            AJU_AUT: zeroPad(
              form.getFieldValue(formNames.generalData.ADJUSTMENT_RATE.name),
              3
            ),
            ANIO: form.getFieldValue(formNames.generalData.VEHICLE_YEAR.name),
            COD_PROD: form.getFieldValue(formNames.information.PRODUCER.name)
              .value,
            ZONA: form.getFieldValue(formNames.information.RISK_AREA.name),
            COD_PROV: form.getFieldValue(
              formNames.userInformation.USER_PROVINCE.name
            ),
            COND_FISCAL: form.getFieldValue(
              formNames.clientInformation.FISCAL_CONDITION.name
            ),
            CUIT: form.getFieldValue(
              formNames.clientInformation.CUIT_CUIL.name
            ),
            RAZON_SOCIAL: form.getFieldValue(
              formNames.clientInformation.COMPANY_NAME_OR_SURNAME.name
            ),
            LOCALIDAD: form.getFieldValue(
              formNames.userInformation.USER_LOCATION.name
            ),
            COS_POS: form.getFieldValue(
              formNames.userInformation.USER_POSTAL_CODE.name
            ),
            MARCA: form.getFieldValue(formNames.generalData.VEHICLE_BRAND.name),
            MODELO: form.getFieldValue(
              formNames.generalData.VEHICLE_VERSION.name
            ),
            PERIODO_COB: form.getFieldValue(
              formNames.generalData.COVERAGE_TIME.name
            ),
            PRODUCTO: form.getFieldValue(
              formNames.generalData.PRODUCT_CODE.name
            ),
            TIPO_VEH: form.getFieldValue(
              formNames.generalData.VEHICLE_TYPE.name
            ),
            SUMA_ASEG: String(
              form.getFieldValue(formNames.generalData.INSURED_AMOUNT.name)
            ),
            USO: form.getFieldValue(formNames.generalData.DESTINY.name),
            COD_ACC: code_acc,
            SUMA_ACC: value_acc,
            COBER_A_COTIZ: [
              form.getFieldValue(formNames.generalData.COVERAGE.name),
            ],
            renovation_id: renovationData
              ? form.getFieldValue(formNames.information.REQUEST_ID.name)
              : undefined,
            REMOLQUE_CC: form.getFieldValue(formNames.generalData.TOWING_SERVICE.name) === 'N' || form.getFieldValue(formNames.generalData.TOWING_SERVICE.name) === 'S' ? form.getFieldValue(formNames.generalData.TOWING_SERVICE.name)?.trim()  : 'S'+form.getFieldValue(formNames.generalData.TOWING_SERVICE.name)?.trim(),
            TIPO_EMISION: form.getFieldValue('TIPO_EMISION'),
          },
        },
      });
      if (!response.quotation.length) {
        setQuotationResponse(response);
        setErrorResponse({
          status_code: 0,
          message: "No hay cotización disponible",
        });
      } else {
        setQuotationResponse(response);
        setErrorResponse({ status_code: 0, message: "" });
      }
    } catch (error: any) {
      setErrorResponse(error);
      messageError({
        context: "getQuotation",
        message: "Error al obtener la cotización",
      });
    }
    setIsLoading(false);
  };

  const onFinish = async (values: IValuesEmisionRenovationForm) => {
    setIsLoading(true);
    if (personType === EnumsValues.PersonType.FISICA) {
      if (form.getFieldsValue().document_number && !isAdult) {
        setIsLoading(false);
        return messageModalError({
          context: "onFinish",
          message: `Debe ser mayor de edad para ${
            renovationData ? "renovar" : "emitir"
          } `,
        });
      }
    }

    const selectedCoverage = coverageType.find(c => c.id_liderar === values.coverage);
    if (
      selectedCoverage &&
      (!uploadedFiles || Object.keys(uploadedFiles).length < 1) &&
      (
        (!renovationData && selectedCoverage.soloRC === EnumsValues.CoverageSoloRC.N) ||
        (
          renovationData &&
          selectedCoverage.ranking && (
            !renovationData.coverage_ranking ||
            renovationData.coverage_ranking < selectedCoverage.ranking
          )
        )
      )
    ) {
      setIsLoading(false);
      return messageModalError({
        context: "onFinish",
        message: "La cobertura requiere al menos una imagen del vehículo.",
      });
    }

    let codigo_de_productor = "";
    if (typeof values.producer === "string") {
      codigo_de_productor = values.producer;
    } else {
      codigo_de_productor = values.producer.value;
    }

    let cuit_number = "";
    if (typeof values.cuit_cuil === "string") {
      cuit_number = values.cuit_cuil;
    } else {
      cuit_number = values.cuit_cuil.value;
    }

    let birth_date = "";
    if (personType === EnumsValues.PersonType.FISICA) {
      birth_date = moment(values.natural_person_birth_date).format(
        FORMAT_DATE_TIME_4
      );
    } else {
      birth_date = moment(values.legal_person_registration_date).format(
        FORMAT_DATE_TIME_4
      );
    }

    const { code_acc, value_acc } = getAccesories();

    const lifePolicyAccepted = () => {
      const showModal = values.fiscal_condition === EnumsValues.FiscalCondition.ConsFinal ||
        values.fiscal_condition === EnumsValues.FiscalCondition.Monotributo;
      return showModal ? true : undefined;
    };

    const emissionParams: IEmissionParams = {
      RENUEVA_POL: renovationData ? values.renew_policy_number : undefined,
      ADIC_GRANIZO: values.additional_hail ? "S" : "N",
      AJU_AUT: zeroPad(Number(values.adjustment_rate), 3),
      ANIO: values.vehicle_year,
      CARROCERIA: values.bodywork_type,
      CHASIS: values.vehicle_chasis_number,
      COBERTURA: billingCode + values.coverage,
      COD_ACC: code_acc,
      COD_PROD: codigo_de_productor,
      COD_PROV: values.user_province,
      COD_TARJETA: values.user_payment_method,
      COND_FISCAL: values.fiscal_condition,
      COS_POS: values.user_postal_code,
      CUIT: cuit_number,
      DEPARTAMENTO: values.user_home_department,
      DOMIC_CALLE: values.user_home,
      DOMIC_NRO: String(values.user_home_number),
      FECHA_NAC: birth_date,
      LIFE_POLICY_ACCEPTED: lifePolicyAccepted(),
      LOCALIDAD: values.user_location,
      MARCA: values.vehicle_brand,
      MODELO: values.vehicle_version,
      MOTOR: values.vehicle_engine_number,
      NRO_DOC: cuit_number.slice(2, -1),
      NRO_PAGO: values.user_payment_number,
      PATENTE: values.vehicle_patent,
      PERIODO_COB: values.coverage_time,
      PISO: values.user_home_floor,
      PRODUCTO: values.product_code,
      RAZON_SOCIAL: values.company_name_or_surname,
      SEXO:
        personType === EnumsValues.PersonType.FISICA
          ? values.natural_person_gender!
          : EnumsValues.Gender.OTROS,
      SUMA_ACC: value_acc,
      SUMA_ASEG: String(values.insured_amount),
      TEL: String(values.user_phone),
      TIPO_COMB: values.fuel_type,
      TIPO_DOC: "DNI",
      TIPO_VEH: values.vehicle_type,
      USO: values.destiny,
      MAIL_ASEG: values.user_email,
      VIG_DESDE: moment(values.start_date).format(FORMAT_DATE_TIME_4),
      ZONA: values.risk_area,
      REMOLQUE_CC: values.REMOLQUE_CC === 'N' || values.REMOLQUE_CC === 'S' ? values.REMOLQUE_CC?.trim()  : 'S'+values.REMOLQUE_CC?.trim(),
      // cak 4
      TIPO_EMISION: values.TIPO_EMISION
    };

    let pledgedCreditorParams: IPledgedCreditorParams;
    if (values.pledge_creditor_yes_or_no) {
      pledgedCreditorParams = {
        business_name_surname: values.pledge_creditor_company_name_or_surname!,
        name: values.pledge_creditor_name!,
        home_street: values.pledge_creditor_home!,
        home_number: String(values.pledge_creditor_home_number!),
        floor: values.pledge_creditor_home_floor!,
        department: values.pledge_creditor_home_department!,
        province_id: values.pledge_creditor_province!,
        postal_code_id: Number(values.pledge_creditor_postal_code!),
      };
    }
    const genderId = (gender_id_liderar: string) => {
      const selectedGender = gendersTypes.find(
        (gender) => gender.id_liderar === gender_id_liderar
      );
      return selectedGender ? selectedGender.id : EnumsValues.GenderId.OTROS;
    };

    let naturalPersonParams: INaturalPersonParams;
    let legalPersonParams: ILegalPersonParams;
    if (personType === EnumsValues.PersonType.FISICA) {
      naturalPersonParams = {
        birth_date: moment(values.natural_person_birth_date).format(
          FORMAT_DATE_TIME_4
        ),
        birth_place: values.natural_person_birth_place!,
        gender_id: genderId(values.natural_person_gender!),
        nationality_id: values.natural_person_nationality!,
      };
    } else {
      legalPersonParams = {
        registration_date: moment(
          values.legal_person_registration_date!
        ).format(FORMAT_DATE_TIME_4),
        registration_number: String(values.legal_person_registration_number!),
        contract_date: moment(values.legal_person_contract_date!).format(
          FORMAT_DATE_TIME_4
        ),
        main_activity: values.legal_person_main_activity!,
      };
    }

    let legal_representative_cuit_number = "";
    let legalRepresentativeParams: ILegalRepresentativeParams;
    if (values.legal_representative_yes_or_no) {
      if (typeof values.cuit_cuil === "string") {
        legal_representative_cuit_number = values.cuit_cuil;
      } else {
        legal_representative_cuit_number = values.cuit_cuil.value;
      }

      legalRepresentativeParams = {
        cuit: legal_representative_cuit_number!,
        name_surname: values.legal_representative_name_surname!,
        home_street: values.legal_representative_home!,
        home_number: String(values.legal_representative_number!),
        floor: values.legal_representative_floor!,
        department: values.legal_representative_department!,
        phone_number: String(values.legal_representative_phone!),
        birth_date: moment(values.legal_representative_birth_date!).format(
          FORMAT_DATE_TIME_4
        ),
        birth_place: values.legal_representative_birth_place!,
        email: values.legal_representative_email!,
        province_id: values.legal_representative_province!,
        postal_code_id: Number(values.legal_representative_postal_code!),
        gender_id: genderId(values.legal_representative_gender!),
        nationality_id: values.legal_representative_nationality!,
      };
    }

    await customRequest({
      mutation: Mutation.createEmission,
      variables: {
        input: emissionParams,
        pledgedCreditor: values.pledge_creditor_yes_or_no
          ? pledgedCreditorParams!
          : null,
        naturalPerson:
          personType === EnumsValues.PersonType.FISICA
            ? naturalPersonParams!
            : null,
        legalPerson:
          personType === EnumsValues.PersonType.JURIDICA
            ? legalPersonParams!
            : null,
        legalRepresentative: values.legal_representative_yes_or_no
          ? legalRepresentativeParams!
          : null,
        quotationId: quotationResponse
          ? quotationResponse?.quotation[0]?.id
          : null,
        renovation_id: renovationData ? renovationData.id : null,
      },
    })
      .then((response: any) => {
        setErrorResponse({
          status_code: 0,
          message: response.error_notice,
        });
        const title =
          formType === EnumsValues.formType.directEmission
            ? "Emisión creada correctamente"
            : "Renovación creada correctamente";

        responseModal(title, response.id_policy, response.id_policy_life, response.obs_policy_life);

        saveImages();
      })
      .catch((error: any) => {
        setErrorResponse(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getInitialProducer = useCallback(() => {
    if (
      user &&
      user.producers &&
      user.producers.length === 1 &&
      formType === EnumsValues.formType.directEmission
    ) {
      return user.producers[0].id_liderar;
    } else {
      return (
        renovationData?.producer_id_liderar || emission?.producer.id_liderar
      );
    }
  }, [user, formType, renovationData, emission]);

  const readOnlyOr =
    formType === EnumsValues.formType.directEmissionReadOnly ||
    formType === EnumsValues.formType.directEmissionHistoricalReadOnly ||
    formType === EnumsValues.formType.renowedQuotesListReadOnly;

  return (
    useCheckAuthority(routeProps) || (
      <div className="renovationFormPage">
        <Form
          layout="vertical"
          onFinish={onFinish}
          form={form}
          wrapperCol={{ span: 24 }}
        >
          <InformationForm
            user={user!}
            initialProducer={getInitialProducer()}
            isRenovation={!!renovationData}
            form={form}
            setSelectedProducer={setSelectedProducer}
            selectedProducer={selectedProducer}
            idsLiderarForMotorcycles={idsLiderarForMotorcycles}
            selectedVehicleType={selectedVehicleType}
            readOnlyOr={readOnlyOr}
          />
          {formType && (
            <ClientInformationForm
              fiscalConditionOptions={fiscalConditionOptions}
              verifyCuit={verifyCuit}
              isLoadingCuitVerification={isLoadingCuitVerification}
              form={form}
              cuit={
                formType === EnumsValues.formType.renovation ||
                formType === EnumsValues.formType.renowedQuotesListReadOnly
                  ? renovationData?.policy?.asegurado[0].cuit
                  : emission?.cuit
              }
              setPersonType={setPersonType}
              ageVerification={ageVerification}
              isRenovation={!!renovationData}
              gendersTypes={gendersTypes}
              isReadOnlyForm={emissionReadOnly}
              formType={formType}
              setFiscalCondition={setFiscalCondition}
              readOnlyOr={readOnlyOr}
            />
          )}

          <UserInformationForm
            paymentOptions={paymentOptions}
            form={form}
            provinceOptionsId={provinceOptionsId}
            renovationData={renovationData}
            provinces={provinces}
            readOnlyOr={readOnlyOr}
            paymentInstallmentTypesOptions={paymentInstallmentTypeOptions}
            producer={selectedProducer}
          />
          <GeneralDataForm
            fuelOptions={fuelOptions}
            vehicleBrand={vehicleBrand}
            brandOptions={brandOptions}
            form={form}
            getBrandModel={getBrandModel}
            vehicleModelOptions={vehicleModelOptions}
            vehicleModel={vehicleModel}
            getYearsByBrandAndModel={getYearsByBrandAndModel}
            yearOptions={yearOptions}
            yearList={yearList}
            getBrandModelByBrandModelYear={getBrandModelByBrandModelYear}
            vehicleVersionOptions={vehicleVersionOptions}
            vehicleVersionList={vehicleVersionList}
            clearOnChangeBrand={clearOnChangeBrand}
            clearOnChangeModel={clearOnChangeModel}
            clearOnChangeYear={clearOnChangeYear}
            getVehicleType={getVehicleType}
            vehicleTypeOptions={vehicleTypeOptions}
            vehicleTypes={vehicleTypes}
            destinyOptions={destinyOptions}
            destinos={destinos}
            clearOnChangeVersion={clearOnChangeVersion}
            clearOnChangeVehicleType={clearOnChangeVehicleType}
            getDestinos={getDestinos}
            bodyWorkOptions={bodyWorkOptions}
            coveragePeriedOptions={coveragePeriedOptions}
            coverageTypeOptions={coverageTypeOptions}
            renovationData={renovationData}
            isRenovation={!!renovationData}
            adjustmentRateOptions={adjustmentRateOptions}
            adjustmentRates={adjustmentRates}
            setAmountOutOfRange={setAmountOutOfRange}
            setAccesoriesError={setAccesoriesError}
            amountOutOfRange={amountOutOfRange}
            accesoriesError={accesoriesError}
            getFuelTypes={getFuelTypes}
            getBodyworkTypes={getBodyworkTypes}
            idsLiderarForMotorcycles={idsLiderarForMotorcycles}
            selectedProducer={selectedProducer}
            fuelTypes={fuelTypes}
            bodyworkTypes={bodyworkTypes}
            setBillingCode={setBillingCode}
            formType={formType}
            emission={emission}
            quotation={quotation}
            setSelectedVehicleType={setSelectedVehicleType}
            setAdditionalHail={setAdditionalHail}
            additionalHail={additionalHail}
            getCoverageTypes={getCoverageTypes}
            readOnlyOr={readOnlyOr}
            setRenovationBlocked={setRenovationBlocked}
            resetCoveragePeriods={resetCoveragePeriods}
          />
          
          {form.getFieldsValue([formNames.clientInformation.CUIT_CUIL.name]) &&
            personType === EnumsValues.PersonType.FISICA && (
              <NaturalPersonForm
                genderOptions={genderOptions}
                minBornTime={minBornTime}
                nationalityOptions={nationalityOptions}
                isRenovation={!!renovationData}
                readOnlyOr={readOnlyOr}
              />
            )}
          {form.getFieldsValue([formNames.clientInformation.CUIT_CUIL.name]) &&
            personType === EnumsValues.PersonType.JURIDICA && (
              <LegalPersonForm readOnlyOr={readOnlyOr} />
            )}
          <VehicleDataForm
            isRenovation={!!renovationData}
            readOnlyOr={readOnlyOr}
          />
          <VehiclePicture
            patent={
              renovationData?.policy?.vehiculo[0].patente || emission?.patent
            }
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
          />
          <PledgeCreditorDataForm
            form={form}
            provinceOptions={provinceOptions}
            formType={formType}
            emission={emission}
            readOnlyOr={readOnlyOr}
          />
          <LegalRepresentativeForm
            form={form}
            genderOptions={genderOptions}
            provinceOptions={provinceOptions}
            nationalityOptions={nationalityOptions}
            gendersTypes={gendersTypes}
            minBornTime={minBornTime}
            isReadOnlyForm={emissionReadOnly}
            formType={formType}
            emission={emission}
            readOnlyOr={readOnlyOr}
          />
          <QuoteForm
            quotationResponse={quotationResponse}
            form={form}
            emissionResponse={errorResponse}
            readOnlyOr={readOnlyOr}
          />
          <div>TODA DECLARACIÓN FALSA O TODA RETICENCIA DE CIRCUNSTANCIAS CONOCIDAS POR EL ASEGURADO, AÚN HECHAS DE BUENA FE, QUE A JUICIO DE PERITOS HUBIESE IMPEDIDO EL CONTRATO O MODIFICADO SUS CONDICIONES, SI EL ASEGURADOR HUBIESE SIDO CERCIORADO DEL VERDADERO ESTADO DEL RIESGO, HACE NULO EL CONTRATO. (ART.5º, PRIMER PÁRRAFO, DE LA LEY DE SEGUROS Nº 17.418)</div>
          <FormButtons
            isLoading={isLoading}
            getQuotation={getQuotation}
            amountOutOfRange={amountOutOfRange}
            accesoriesError={accesoriesError}
            emissionReadOnly={emissionReadOnly}
            formType={formType}
            quotationId={quotationId}
            confirmModal={() => {
              if (validateForm()) {
                setModalState({isOpen: true})
              }
            }}
            onFinish={onFinish}
            fiscalCondition={fiscalCondition}
            renovationBlocked={renovationBlocked}
          />
        </Form>
        <ModalLifePolicy
          modalState={modalState}
          setModalState={setModalState}
          onOk={() => {
            setModalState({isOpen: false})
            form.submit();
          }}
        ></ModalLifePolicy>
      </div>
    )
  );
};

export default RenovationFormPage;
